'use client'
import Image from 'next/image'
import HeaderSearch from '@/travelmde/components/Common/HeaderSearch'
import HeaderPopUpMobile from '@/travelmde/components/Common/HeaderPopUpMobile'
import { usePathname } from 'next/navigation'
import Link from 'next/link'

const logoImage = {
  src: 'https://media.travelm.de/travelm-bucket/LogoLarge.webp',
  width: 200,
  height: 50,
}

const flags = {
  eng: {
    src: 'https://media.travelm.de/travelm-bucket/engFlag.webp',
    width: 30,
    height: 20,
  },
  english: {
    src: 'https://media.travelm.de/travelm-bucket/Frame%202(1).webp',
    width: 30,
    height: 20,
  },
  german: {
    src: 'https://media.travelm.de/travelm-bucket/Flag_of_germany_800_480.webp',
    width: 30,
    height: 20,
  },
  french: {
    src: 'https://media.travelm.de/travelm-bucket/Flag_of_France.webp',
    width: 30,
    height: 20,
  },
  spanish: {
    src: 'https://media.travelm.de/travelm-bucket/Spain_flag_300.webp',
    width: 30,
    height: 20,
  },
}

const airplaneIcon = (
  <svg width="22" height="10" viewBox="0 0 22 10" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M1.99566 7.21993C2.34983 7.70327 2.89983 7.9741 3.479 7.9741C3.62066 7.9741 3.77066 7.95743 3.91233 7.9241C5.37066 7.56993 8.08733 6.89493 10.7207 6.16577L9.45816 9.14493C9.39983 9.2741 9.4165 9.41993 9.4915 9.5366C9.57066 9.65327 9.69983 9.7241 9.8415 9.7241C10.8873 9.7241 11.879 9.2616 12.5498 8.45327C13.3915 7.4491 14.4498 6.04493 14.8998 4.91993C15.1665 4.82827 15.4207 4.74077 15.6582 4.65743C18.229 3.73243 19.779 3.11577 20.6123 2.4991C21.004 2.20327 21.1707 1.7116 21.0415 1.24493C20.9082 0.778266 20.5082 0.449099 20.0248 0.407432L18.6498 0.286599C18.154 0.244933 17.6498 0.319932 17.1915 0.503265L12.0082 2.56577C10.579 2.11993 8.2415 1.8241 6.94566 1.6866C6.14983 1.5991 5.37483 1.88243 4.82066 2.45743C4.72066 2.55743 4.679 2.70327 4.71233 2.84077C4.74566 2.97827 4.84566 3.09077 4.979 3.14077L7.954 4.18243L4.5665 5.52827L2.02066 3.95743C1.9165 3.89077 1.78733 3.87827 1.67066 3.91993L0.533163 4.30327C0.408163 4.34493 0.308163 4.44493 0.270663 4.5741C0.228996 4.6991 0.253996 4.8366 0.333163 4.94493L1.99566 7.21993Z"
      fill="#FE9200"
    />
  </svg>
)

const HeaderMain = ({ lang, germancategories, maincategories, dictionary }) => {
  const pathname = usePathname()

  const currentPath = pathname.split(lang)[1] || '/'

  let matchingSlug = {}
  // all the menu slugs are in german
  // we want to get the slug in the language of the user
  if (maincategories && germancategories) {
    for (const cat of maincategories) {
      const catId = cat?.id
      // find the matching german cat
      const germanCat = germancategories.find((cat) => cat?.id === catId)
      if (cat?.slug) matchingSlug[cat?.slug] = germanCat?.slug
    }
  }

  let languages = [
    {
      shortkey: 'DE',
      name: dictionary?.language?.de || 'Deutsch',
      flag: flags.german,
      language: 'de-DE',
    },
  ]
  if (dictionary) {
    languages = [
      ...languages,
      { shortkey: 'EN', name: dictionary.language.en, flag: flags.english, language: 'en-US' },
      { shortkey: 'FR', name: dictionary.language.fr, flag: flags.french, language: 'fr-FR' },
      { shortkey: 'ES', name: dictionary.language.es, flag: flags.spanish, language: 'es-ES' },
    ]
  }

  return (
    <div className="header__main">
      <div className="header__outer container">
        <div className="header__logo dflex">
          <Link href={`/${lang}`}>
            <Image
              className="logo__image"
              src={logoImage?.src}
              alt="Logo Image"
              width={logoImage.width}
              height={logoImage.height}
            />
          </Link>
        </div>
        <div className="header__menu">
          <ul className="menu__list">
            <li className={`${currentPath === '/' ? 'active' : ''}`}>
              <Link href={`/${lang}`}>{currentPath === '/' ? airplaneIcon : null} Home</Link>
            </li>
            {germancategories &&
              maincategories &&
              maincategories.map((category) => (
                <li
                  key={category.id}
                  className={`${currentPath.includes(matchingSlug[category?.slug]) ? 'active' : ''}`}
                >
                  <Link
                    href={`/${lang}/main-category/${matchingSlug[category?.slug]}/1/${
                      matchingSlug[category.slug]?.includes('events--prides') ||
                      matchingSlug[category.slug]?.includes('gay-map') ||
                      matchingSlug[category.slug]?.includes('hotels') ||
                      matchingSlug[category.slug]?.includes('saunas')
                        ? 'default'
                        : ''
                    }`}
                  >
                    {currentPath.includes(matchingSlug[category.slug]) ? airplaneIcon : null}
                    {category?.title}
                  </Link>
                </li>
              ))}
          </ul>
        </div>
        {languages && languages.length > 1 && (
          <HeaderSearch lang={lang} languages={languages} title={dictionary.search.title} />
        )}
        {germancategories && maincategories && (
          <HeaderPopUpMobile
            lang={lang}
            logoImage={logoImage}
            languages={languages}
            airplaneIcon={airplaneIcon}
            matchingSlug={matchingSlug}
            maincategories={maincategories}
            currentPath={currentPath}
          />
        )}
      </div>
    </div>
  )
}

export default HeaderMain
